/**
 * Device Detection Utility
 *
 * Parses user agent and collects device/browser information
 */

/**
 * Parse browser name and version from user agent
 */
function parseBrowser(userAgent) {
  const ua = userAgent.toLowerCase();

  // Order matters - more specific matches first
  const browsers = [
    { name: 'Edge', pattern: /edg(?:e|a|ios)?\/(\d+)/ },
    { name: 'Opera', pattern: /(?:opera|opr)\/(\d+)/ },
    { name: 'Chrome', pattern: /(?:chrome|crios)\/(\d+)/ },
    { name: 'Firefox', pattern: /(?:firefox|fxios)\/(\d+)/ },
    { name: 'Safari', pattern: /version\/(\d+).*safari/ },
    { name: 'IE', pattern: /(?:msie |trident.*rv:)(\d+)/ }
  ];

  for (const browser of browsers) {
    const match = ua.match(browser.pattern);
    if (match) {
      return {
        browser: browser.name,
        browserVersion: match[1]
      };
    }
  }

  return { browser: 'Unknown', browserVersion: '' };
}

/**
 * Parse OS name and version from user agent
 */
function parseOS(userAgent) {
  const ua = userAgent;

  const osList = [
    { name: 'iOS', pattern: /iP(?:hone|od|ad).*OS (\d+[._]\d+)/ },
    { name: 'Android', pattern: /Android (\d+(?:\.\d+)?)/ },
    { name: 'Windows', pattern: /Windows NT (\d+\.\d+)/ },
    { name: 'macOS', pattern: /Mac OS X (\d+[._]\d+)/ },
    { name: 'Linux', pattern: /Linux/ },
    { name: 'ChromeOS', pattern: /CrOS/ }
  ];

  for (const os of osList) {
    const match = ua.match(os.pattern);
    if (match) {
      let version = match[1] || '';
      // Normalize version separators
      version = version.replace(/_/g, '.');

      // Map Windows NT versions to friendly names
      if (os.name === 'Windows') {
        const ntVersions = {
          '10.0': '10/11',
          '6.3': '8.1',
          '6.2': '8',
          '6.1': '7',
          '6.0': 'Vista',
          '5.1': 'XP'
        };
        version = ntVersions[version] || version;
      }

      return { os: os.name, osVersion: version };
    }
  }

  return { os: 'Unknown', osVersion: '' };
}

/**
 * Detect device type based on user agent and screen size
 */
function getDeviceType(userAgent, screenWidth) {
  const ua = userAgent.toLowerCase();

  // Check for mobile first
  if (/iphone|ipod|android.*mobile|windows phone|blackberry|webos/i.test(ua)) {
    return 'mobile';
  }

  // Check for tablet
  if (/ipad|android(?!.*mobile)|tablet/i.test(ua)) {
    return 'tablet';
  }

  // Fall back to screen size if user agent is inconclusive
  if (screenWidth) {
    if (screenWidth < 768) return 'mobile';
    if (screenWidth < 1024) return 'tablet';
  }

  return 'desktop';
}

/**
 * Get complete device information
 */
export function getDeviceInfo() {
  const userAgent = navigator.userAgent;
  const { browser, browserVersion } = parseBrowser(userAgent);
  const { os, osVersion } = parseOS(userAgent);

  const screenWidth = window.screen?.width || 0;
  const screenHeight = window.screen?.height || 0;
  const viewportWidth = window.innerWidth || document.documentElement?.clientWidth || 0;
  const viewportHeight = window.innerHeight || document.documentElement?.clientHeight || 0;

  return {
    userAgent,
    browser,
    browserVersion,
    os,
    osVersion,
    deviceType: getDeviceType(userAgent, screenWidth),
    screenWidth,
    screenHeight,
    viewportWidth,
    viewportHeight,
    language: navigator.language || 'en-US',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  };
}

/**
 * Generate a unique session ID (UUID v4)
 */
export function generateSessionId() {
  // Use crypto.randomUUID if available (modern browsers)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get or create session ID from sessionStorage
 */
export function getSessionId() {
  const STORAGE_KEY = 'analytics_session_id';
  let sessionId = sessionStorage.getItem(STORAGE_KEY);

  if (!sessionId) {
    sessionId = generateSessionId();
    sessionStorage.setItem(STORAGE_KEY, sessionId);
  }

  return sessionId;
}

/**
 * Clear session ID (call on logout)
 */
export function clearSessionId() {
  sessionStorage.removeItem('analytics_session_id');
}

export default {
  getDeviceInfo,
  generateSessionId,
  getSessionId,
  clearSessionId
};
